import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  message,
  Spin,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { 
  useGetUsimOrderbyIdQuery, 
  useGetUsimOrderIccidInventoryQuery,
  useGetUsimInventoryByMobileQuery,
  usePutUsimOrdersMutation 
} from "../../../api/usim/orders/UsimOrdersApi";
import { MarketOrderItem } from "../../../api/usim/orders/MarketOrderItem";
import { UsimInventoryItem } from "../../../api/usim/inventory/inventoryItem";
import { Auth } from 'aws-amplify';
import { UsimOrderSubmitData } from "../../../api/usim/orders/UsimOrderItem";
import { USimStatus, toUSimStatus } from "./usimUtils";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Checkbox, Tooltip } from "antd";
import { useGetUsimSkuByTypeQuery } from "../../../api/usim/uploads/products/productSkuApi";
import { useGetProductTypesQuery } from "../../../api/usim/uploads/products/productTypeApi";
import { getInventoryStatus, getColumns, TableRowData } from "../orders/new/orderUtilities";
import { ProductTypeDropdown } from "./ProductTypeDropdown";
import { UsimSkuDropdown } from "./usimSkuDropdown";
const { TextArea } = Input;
const { Title } = Typography;

interface RetailOrderFormData {
  orderProcessedDate: Dayjs | null;
  orderNumber: string;
  quantity: number;
  activationDate: Dayjs | null;
  productType: string;
  productPlan: string;
  comment: string;
  planRecharge?: boolean;
}


export default function EditRetailorderPage(props: { orderNumber: string, shopId: string }) {
  const [form] = Form.useForm();
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [orderNumber, setOrderNumber] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(0);
  const [currentIccid, setCurrentIccid] = useState<string>("");
  const [currentMobileNumber, setCurrentMobileNumber] = useState<string>("");
  const [tempOrderNumber, setTempOrderNumber] = useState<string>("");
  const [tempIccid, setTempIccid] = useState<string>("");
  const [tempMobileNumber, setTempMobileNumber] = useState<string>("");
  const [tempRowKey, setTempRowKey] = useState<string>("");
  const [putUsimOrder] = usePutUsimOrdersMutation();
  const [marketOrder, setMarketOrder] = useState<MarketOrderItem>({} as MarketOrderItem);
  const [isRechargePlan, setIsRechargePlan] = useState<boolean>(false);
  const [formData, setFormData] = useState<RetailOrderFormData>({
    orderProcessedDate: null,
    orderNumber: "",
    quantity: 0,
    activationDate: null,
    productType: "",
    productPlan: "",
    comment: "",
  });
  const [deleteProducts, setDeleteProducts] = useState<any[]>([]);

  const [selectedProductType, setSelectedProductType] = useState<string>("");
  const { data: productPlansForType, isFetching: productPlansForTypeFetching } = useGetUsimSkuByTypeQuery(selectedProductType, { skip: !selectedProductType });
  const { data: productTypes } = useGetProductTypesQuery();

  const productTypesOptions = React.useMemo(() => {
    if (!productTypes) return [];
    return productTypes.filter(item => item.status !== false).map(item => ({
      label: item.name,
      value: item.name,
      iccid: item.iccid,
      id: item.id,
      hasActivation: item.activationDate ?? false
    }));
  }, [productTypes]);

  const productPlansOptions = React.useMemo(() => {
    if (!productPlansForType) return [];
    return productPlansForType.filter(item => item.status).map(item => ({
      label: item.name,
      value: item.name,
      type: item.typeName
    }));
  }, [productPlansForType]);

  // RTK Query hook for fetching order by ID
  const { data: orderData, error: orderError, isLoading } = useGetUsimOrderbyIdQuery(
    orderNumber,
    { skip: !orderNumber } // Skip the query if orderNumber is empty
  );
    // RTK Query hook for fetching ICCID inventory details
  const { data: iccidInventory, error: iccidInventoryError, isLoading: iccidInventoryLoading } = useGetUsimOrderIccidInventoryQuery(
    currentIccid,
    { skip: !currentIccid } // Skip the query if no ICCID is being looked up
  );

   const { data: mobileInventory, error: mobileInventoryError, isLoading: mobileInventoryLoading } = useGetUsimInventoryByMobileQuery(
    currentMobileNumber,
    { skip: !currentMobileNumber } 
  );

  // Prefill by props when component becomes visible
  useEffect(() => {
    if (props.orderNumber && props.orderNumber !== orderNumber) {
      setOrderNumber(props.orderNumber);
      setTempOrderNumber(props.orderNumber);
      form.setFieldsValue({ orderNumber: props.orderNumber });
    }
  }, [props.orderNumber]);

  // Prefill form and table when order data loads
  useEffect(() => {
    if (!orderData) return;

    // Map top-level fields into the form; leave activation/product fields empty
    const processedDateStr = (orderData as any).orderProcessedDate || (orderData as any).createdAt || "";
    const processedDate = processedDateStr ? dayjs(processedDateStr) : null;
    const ordNum = (orderData as any).OrderNumber || (orderData as any).orderNumber || props.orderNumber || "";
    const qty = (orderData as any).quantity ?? 0;

    const nextForm: RetailOrderFormData = {
      orderProcessedDate: processedDate,
      orderNumber: ordNum,
      quantity: qty,
      activationDate: null,
      productType: "",
      productPlan: "",
      comment: "",
    };

    // Set form values and local state
    form.setFieldsValue(nextForm);
    setFormData(nextForm);
    // console.log()
    // Build table rows from productDetails
    const details = (orderData as any).productDetails || [];
    const rows: TableRowData[] = details.map((item: any, idx: number) => {
      const actDateStr: string | undefined = item.activationDate || item.activation_date;
      const actDate = actDateStr ? dayjs(actDateStr) : null;

      return {
        key: `row-${idx}`,
        iccid: item.iccid || "",
        mobileNumber: item.mobileNumber || "",
        productType: item.productType || item.type || "",
        productPlan: item.planName || item.sku || "",
        status: toUSimStatus(item.status),
        activationDate: actDate,
        inventoryId: item.inventoryId || item.inventory_id || "",
        puk: item.puk ||"",
        hasActivation: item.isActivation ?? false,
      } as TableRowData;
    });
    setTableData(rows);
  }, [orderData]);


  function validIccidToUse(inventoryItem: UsimInventoryItem) {
    if (inventoryItem.ordernumber) {
      const isSameOrder = Array.isArray(inventoryItem.ordernumber) 
        ? inventoryItem.ordernumber.includes(orderNumber)
        : inventoryItem.ordernumber === orderNumber;

      if (isSameOrder || isRechargePlan) {
        return true;
      }

      Modal.confirm({
        title: "ICCID is Already Used in the order",
        content: <div>
          <p>Order Number: {Array.isArray(inventoryItem.ordernumber) ? inventoryItem.ordernumber.join(", ") : inventoryItem.ordernumber}</p>
          <p>ICCID: {inventoryItem.ICCID}</p>
        </div>
      });
      // clear the prefilled for this row only
      setTableData(prev => prev.map((row, index) => {
        if (row.key === tempRowKey) {
          return {
            ...row,
            mobileNumber: "",
            productType: "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: row.activationDate, // Preserve existing activation date
            inventoryId: "",
            hasActivation: false,
          };
        }
        return row;
      }));
      return false;
    }
    return true;
  }

  function setInventoryData(inventoryItem: UsimInventoryItem){
    // Clear the current ICCID/mobile number states to prevent stale data
    setCurrentIccid("");
    setCurrentMobileNumber("");
    setTempIccid("");
    setTempMobileNumber("");

    if(!validIccidToUse(inventoryItem)){
      return;
    }

    let status = getInventoryStatus(productTypesOptions, inventoryItem);
    
    setTableData(prev => prev.map((row, index) => {
      if (row.key === tempRowKey) {
        // For mobile number lookup, update ICCID without triggering the ICCID API
        const tempIccid = currentMobileNumber ? String(inventoryItem.ICCID || "") : row.iccid;
        const localProductType = productTypesOptions?.find(x => x.id === inventoryItem.typeId)?.label || "";
        
        const baseUpdate = {
          ...row,
          iccid: tempIccid,
          mobileNumber: inventoryItem.mobileNumber || "",
          status: row.activationDate ? "Pending-Activation" : status,
          productType: localProductType || inventoryItem.productType || "",
          inventoryId: inventoryItem.Id || "",
          activationDate: row.activationDate, // Preserve existing activation date
        };

        if (inventoryItem.planName !== "-") {
          baseUpdate.productPlan = inventoryItem.planName || "";
        } else {
          var tempTypeName = productTypes?.find(x => x.id === inventoryItem.typeId)?.name || "";
          setSelectedProductType(tempTypeName);
        }

        return baseUpdate;
      }
      return row;
    }));
  }

  function clearProductRow(){
setTableData(prev => prev.map(row => {
        if (row.iccid === currentIccid) {
          return {
            ...row,
            mobileNumber: "",
            productType: "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: row.activationDate, // Preserve existing activation date
            inventoryId : "",
            hasActivation: false,
          };
        }
        return row;
      }));
      //  message.warning('No inventory found for this ICCID');
  }
  // Handle ICCID inventory data
  useEffect(() => {
    if (iccidInventory && iccidInventory.length > 0 && currentIccid ) {
      const inventoryItem = iccidInventory[0];
      console.log("inventoryItem", inventoryItem)
      setInventoryData(inventoryItem);
    } else if (iccidInventory && iccidInventory.length === 0 && currentIccid) {
      // Clear row data if API returns empty result
      clearProductRow();
     
    }
  }, [iccidInventory, currentIccid]);

    useEffect(() => {
    if (mobileInventory && mobileInventory.length > 0 && currentMobileNumber) {
      const inventoryItem = mobileInventory[0];
      console.log("inventoryItem", inventoryItem);
      setInventoryData(inventoryItem);
    } else if (mobileInventory && mobileInventory.length === 0 && currentMobileNumber) {
      // Clear row data if API returns empty result
      clearProductRow();
      message.warning('No inventory found for this mobile number');
    }
  }, [mobileInventory, currentMobileNumber]);

  // Show error message if ICCID lookup fails
  useEffect(() => {
    if (iccidInventoryError) {
      // Clear row data if API returns error
      setTableData(prev => prev.map(row => {
        if (row.iccid === currentIccid) {
          return {
            ...row,
            mobileNumber: "",
            productType: "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: row.activationDate, // Preserve existing activation date
            inventoryId : "",
            hasActivation: false,
          };
        }
        return row;
      }));
      message.error('Failed to fetch ICCID details');
    }
  }, [iccidInventoryError, currentIccid]);

  // Show error message if mobile number lookup fails
  useEffect(() => {
    if (mobileInventoryError) {
      // Clear row data if API returns error
      setTableData(prev => prev.map(row => {
        if (row.mobileNumber === currentMobileNumber) {
          return {
            ...row,
            iccid: "",
            mobileNumber: "",
            productType: "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: row.activationDate, // Preserve existing activation date
            inventoryId: "",
            hasActivation: false,
          };
        }
        return row;
      }));
      message.error('Failed to fetch mobile number details');
    }
  }, [mobileInventoryError, currentMobileNumber]);

  // Generate table rows based on quantity while preserving existing data
  useEffect(() => {
    setTableData(prevData => {
      const newRows: TableRowData[] = [];
      for (let i = 0; i < formData.quantity; i++) {
        // If row exists, preserve its data
        if (i < prevData.length) {
          newRows.push({
            ...prevData[i],
            // Only update these fields if they're empty
            productType: prevData[i].productType || formData.productType || "",
            productPlan: prevData[i].productPlan || formData.productPlan || "",
            activationDate: prevData[i].activationDate || formData.activationDate
          });
        } else {
          // Add new row with default values
          newRows.push({
            key: `row-${i}`,
            iccid: "",
            mobileNumber: "",
            productType: formData.productType || "",
            productPlan: formData.productPlan || "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: formData.activationDate,
            inventoryId: "",
            puk: "",
            hasActivation: false,
          });
        }
      }
      return newRows;
    });
  }, [formData.quantity, formData.productType, formData.productPlan, formData.activationDate]);

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    // If order number changed, update temp state only
    if (changedValues.orderNumber !== undefined) {
      setTempOrderNumber(changedValues.orderNumber);
      return;
    }
    console.log("all values", allValues)
    setFormData(allValues);
  };

  // Handle order number input blur
  const handleOrderNumberBlur = () => {
    if (tempOrderNumber !== orderNumber) {
      setOrderNumber(tempOrderNumber);
    }
  };

  const handleSubmit = async (values: RetailOrderFormData) => {
    if (!values.orderNumber) {
      message.error('Order number is required');
      return;
    }

    if (!values.quantity || values.quantity <= 0) {
      message.error('Quantity must be greater than 0');
      return;
    }

    // Check if all required ICCIDs are filled
    // const filledRows = tableData.filter(row => row.iccid.trim() !== '');
    // if (filledRows.length !== values.quantity) {
    //   message.error(`Please fill in all ${values.quantity} ICCID(s)`);
    //   return;
    // }

    const now = new Date().toISOString();
    let order_status: string = USimStatus.PENDING_PROCESSING;
    tableData.forEach(row => {
      order_status = toUSimStatus(row.status);
    });
    // let session = await Auth.currentUserInfo();
    // const uname = session.username || "";
    // console.log("uname is", uname)
    // shopId map
    //orderData.productDetails[index].shopId
    // console.log("order data is", orderData)
    const submitData: UsimOrderSubmitData = {
      OrderNumber: values.orderNumber,
      orderProcessedDate: values.orderProcessedDate?.toISOString() || now,
      type: values.productType,
      description: values.comment || '',
      sku: values.productPlan,
      quantity: values.quantity,
      status: order_status,
      processedBy: "",
      deleteProducts: deleteProducts,
      productDetails: tableData.map((row,index) => ({
        iccid: row.iccid,
        mobileNumber: row.mobileNumber,
        productType: row.productType,
        sku: row.productPlan,
        status: toUSimStatus(row.status),
        activationDate: row.activationDate ? dayjs(row.activationDate).format('YYYY-MM-DD') : undefined,
        inventoryId : row.inventoryId,
        puk: row.puk || "",
        shopId: orderData?.productDetails[index]?.shopId || "",
        hasActivation: row.hasActivation,
      })),
      orderData: marketOrder,
    };
   if(submitData.productDetails.length == submitData.quantity && submitData.quantity > 0){
     for (const productDetail of submitData.productDetails) {
      if (!productDetail.productType || productDetail.productType == "") {
        message.error('Product type is required for each product detail');
        return;
      }
    }
   }

    try {
      // console.log("submit data", submitData)
      console.log(JSON.stringify(submitData).length);
      const response = await putUsimOrder(submitData).unwrap();
      message.success('Order updated successfully');
    } catch (error: any)  {
      console.error('Error submitting order:', error);
      message.error(error.data.message || 'Failed to submit order');
    }
  };

  const onClickSubmit = async () => {
    try {
      const values = await form.validateFields();
      await handleSubmit(values);
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Validation failed');
      }
    }
  };


  const handleTableDataChange = (
    key: string,
    field: keyof TableRowData,
    value: string
  ) => {
    setTableData((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentKey: string,
    field: keyof TableRowData
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Find current row index
      const currentIndex = tableData.findIndex((row) => row.key === currentKey);
      if (currentIndex === -1) return;

      // Find next row
      const nextRow = tableData[currentIndex + 1];
      if (nextRow) {
        // Focus on the same field in the next row
        const nextInput = document.querySelector(
          `[data-row-key="${nextRow.key}"][data-field="${field}"]`
        ) as HTMLElement;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handleSelectKeyPress = (
    e: React.KeyboardEvent,
    currentKey: string,
    field: keyof TableRowData
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Find current row index
      const currentIndex = tableData.findIndex((row) => row.key === currentKey);
      if (currentIndex === -1) return;

      // Find next row
      const nextRow = tableData[currentIndex + 1];
      if (nextRow) {
        // Focus on the same field in the next row
        const nextInput = document.querySelector(
          `[data-row-key="${nextRow.key}"][data-field="${field}"]`
        ) as HTMLElement;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handleDeleteRow = (key: string) => {
    setTableData(prev => {
      const rowToDelete = prev.find(row => row.key === key);
      if (rowToDelete) {
        setDeleteProducts(dp => [...dp, rowToDelete]);
      }
      return prev.filter(row => row.key !== key);
    });
  };

  const columns: ColumnsType<TableRowData> = getColumns({
    handleDeleteRow,
    handleTableDataChange,
    setTempIccid,
    setTempRowKey,
    handleKeyPress,
    tempIccid,
    currentIccid,
    setCurrentIccid,
    tempMobileNumber,
    currentMobileNumber,
    setCurrentMobileNumber,
    setTempMobileNumber,
    productTypesOptions,
    setSelectedProductType,
    handleSelectKeyPress,
    productPlansOptions,
    productPlansForTypeFetching
  });

  return (
    <Spin spinning={isLoading} tip="Loading order...">
    <div style={{ padding: "24px" }}>
      <Title level={2}>Edit Retail Order</Title>
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onValuesChange={handleFormValuesChange}
        initialValues={formData}
        onFinish={handleSubmit}
        style={{ maxWidth: 600 }}
      >
        <Form.Item label="Order Processed Date" name="orderProcessedDate"  getValueProps={(i) => ({value: dayjs(i)})}>
          <DatePicker style={{ width: "100%" }} placeholder="Select date" format="DD-MMM-YYYY" />
        </Form.Item>

        <Form.Item
          label={
            <span>
              <Tooltip title="Select if this order is a top-up (second recharge)." style={{ cursor: 'pointer', color: '#111111', backgroundColor: '#F2F2F2' }}>
                <InfoCircleOutlined style={{ marginRight: 8 }} />
              </Tooltip>
              Recharge Plan
            </span>
          }
          name="planRecharge"
        >
          <Checkbox checked={isRechargePlan} onChange={(e) => setIsRechargePlan(e.target.checked)} />
        </Form.Item>

        <Form.Item label="Order Number" name="orderNumber" rules={[{ required: true, message: "Order Number is required" }]}>
          <Input 
            placeholder="Enter order number" 
            onChange={(e) => {
              const value = e.target.value.trim();
              setTempOrderNumber(value);
            }}
            onBlur={handleOrderNumberBlur}
          />
        </Form.Item>

        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[{ required: true, message: "Quantity is required" }]}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder="Enter quantity"
            onChange={(value) => {
              setQuantity(value ?? 0);
            }}
          />
        </Form.Item>

        <Form.Item label="Activation Date" name="activationDate">
          <DatePicker
            style={{ width: "100%" }}
            placeholder="Select activation date"
            format="DD-MMM-YYYY"
          />
        </Form.Item>

        <Form.Item label="Product Type" name="productType">
            <ProductTypeDropdown
                placeholder="Select product Type"
              />
        </Form.Item>

        <Form.Item label="Product SKU" name="productPlan" className="w-full">
                 <UsimSkuDropdown
                placeholder="Select product SKU"
                 style={{ width: "100%" }}
                 typeName={formData.productType}
              />
        </Form.Item>

        <Form.Item label="Comment" name="comment">
          <TextArea rows={3} placeholder="Enter comments" />
        </Form.Item>
      </Form>
      
      {/* Table Section */}
      <Title level={3}>Product Details</Title>
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        size="small"
        locale={{
          emptyText: "Enter quantity above to add product details",
        }}
      />
      {/* Action Buttons */}
      <Flex gap={24} justify="flex-end" style={{ marginTop: 16 }}>
 {/* <Button onClick={handleReset}>Reset</Button> */}
        <Button type="primary" onClick={onClickSubmit} style={{ width: "120px" }}>
          Submit
        </Button>
      </Flex>
    </div>
    </Spin>
  );
}
